/**
 * 群馬県の背景ツアー「群馬めぐり」。10か所、それぞれに3つの豆知識。
 *
 * 書いてはいけないこと。
 * - 富岡製糸場は「企業のPRや宣伝活動のための撮影」に申請が要ると公式が明記している。
 *   事前の確認なしには扱えないので入れていない。
 * - 草津白根山の湯釜は、火山規制のためいま立ち寄ることができないので入れていない。
 * - 高崎白衣大観音は昭和11年の像で作者が確認できなかったので入れていない。
 * - 尾瀬ヶ原は福島側で採っているので、群馬側では採っていない。
 * - 一ノ倉沢は周辺に遭難者の慰霊碑があるとされるが公式で確認できなかった。岩壁と雪渓だけを描く。
 * - 赤城山は大沼の湖畔に赤城神社の鳥居があるので、覚満淵の湿原と木道を選んでいる。
 *   氷上のワカサギ釣りのテントも事業者のものなので描かない。
 * - 草津の湯畑は周囲に土産屋や飲食店の看板が密集するので、店名やロゴが入らない構図にする。
 *   木枠の湯樋や湯の花は公式で裏が取れなかったので書かない。
 * - 伊香保の石段には与謝野晶子の詩が刻まれている。文字が読める形では描かない。
 * - 榛名湖の冬のイルミネーションは主催事業なので描かない。
 * - 妙義山は「日本三大奇景」ではなく「日本三大奇勝」と書く。
 */

import type { StageTour } from '../types';

export const gunmaSceneryTour: StageTour = {
  scenes: [
    {
      id: 'harunako',
      name: '榛名湖と榛名富士',
      area: '高崎市',
      image: '/assets/scenery/gunma/scenes/01_harunako.webp',
      hotspots: [
        {
          id: 'harunako-altitude',
          x: 0.28,
          y: 0.4,
          title: '標高1100メートルの湖',
          body: '榛名湖は標高1,100メートルにある。',
          source: 'https://www.city.takasaki.gunma.jp/site/sightseeing/4625.html',
          mapQuery: '榛名湖',
        },
        {
          id: 'harunako-origin',
          x: 0.58,
          y: 0.3,
          title: '火山活動で生まれた',
          body: '榛名山の火山活動によって生まれた湖である。',
          source: 'https://www.city.takasaki.gunma.jp/site/sightseeing/4625.html',
          mapQuery: '榛名富士',
        },
        {
          id: 'harunako-autumn',
          x: 0.8,
          y: 0.7,
          title: '秋は外輪山が真っ赤に',
          body: '秋になると榛名富士や外輪山が紅葉で真っ赤に染まる。',
          source: 'https://www.city.takasaki.gunma.jp/site/sightseeing/4625.html',
          mapQuery: '高崎市 榛名湖',
        },
      ],
    },
    {
      id: 'chatsubomigoke',
      name: 'チャツボミゴケ公園 穴地獄',
      area: '中之条町',
      image: '/assets/scenery/gunma/scenes/02_chatsubomigoke.webp',
      hotspots: [
        {
          id: 'chatsubomigoke-ramsar',
          x: 0.28,
          y: 0.3,
          title: '芳ヶ平湿地群として登録',
          body: '芳ヶ平湿地群は平成27年5月28日、ラムサール条約の「国際的に重要な湿地に係る登録簿」に登録された。',
          source: 'https://www.town.nakanojo.gunma.jp/soshiki/12/13554.html',
          mapQuery: 'チャツボミゴケ公園',
        },
        {
          id: 'chatsubomigoke-tennen',
          x: 0.55,
          y: 0.6,
          title: '鉄鉱生成地として天然記念物',
          body: '国の天然記念物としての指定名称は「六合チャツボミゴケ生物群集の鉄鉱生成地」で、平成29年2月9日に指定された。',
          source: 'https://www.town.nakanojo.gunma.jp/soshiki/12/13554.html',
          mapQuery: '中之条町 穴地獄',
        },
        {
          id: 'chatsubomigoke-mine',
          x: 0.78,
          y: 0.8,
          title: 'もとは鉄山の採掘跡',
          body: 'チャツボミゴケの群生は東アジア最大級と言われる。かつて「穴地獄」と呼ばれた旧群馬鉄山の採掘跡地で、強酸性の鉱泉が水温20度ほどで湧き出している。',
          source: 'https://www.town.nakanojo.gunma.jp/soshiki/12/13554.html',
          mapQuery: 'チャツボミゴケ',
        },
      ],
    },
    {
      id: 'fukiware',
      name: '吹割の滝',
      area: '沼田市',
      image: '/assets/scenery/gunma/scenes/03_fukiware.webp',
      hotspots: [
        {
          id: 'fukiware-size',
          x: 0.3,
          y: 0.35,
          title: '高さ7メートル、幅30メートル',
          body: '高さ7メートル、幅30メートルにおよぶ。縦に落ちるのではなく、横に広がる滝である。',
          source: 'https://www.city.numata.gunma.jp/kanko/fukiware/1003062.html',
          mapQuery: '吹割の滝',
        },
        {
          id: 'fukiware-name',
          x: 0.58,
          y: 0.62,
          title: '岩が吹き割れたよう',
          body: '凝灰岩・花崗岩の川床の上を流れる片品川の清流が、岩質の軟らかい部分を浸食して多数の割れ目を生じ、あたかも巨大な岩が吹き割れたように見えるところから名が生まれた。',
          source: 'https://www.city.numata.gunma.jp/kanko/fukiware/1003062.html',
          mapQuery: '沼田市 吹割の滝',
        },
        {
          id: 'fukiware-designation',
          x: 0.8,
          y: 0.8,
          title: '昭和11年に指定',
          body: '昭和11年12月16日、当時の文部省から「天然記念物及び名勝」に指定された。「東洋のナイアガラ」とも呼ばれる。',
          source: 'https://www.city.numata.gunma.jp/kanko/fukiware/1003062.html',
          mapQuery: '吹割の滝 観瀑台',
        },
      ],
    },
    {
      id: 'ichinokurasawa',
      name: '谷川岳 一ノ倉沢',
      area: 'みなかみ町',
      image: '/assets/scenery/gunma/scenes/04_ichinokurasawa.webp',
      hotspots: [
        {
          id: 'ichinokurasawa-rock',
          x: 0.3,
          y: 0.3,
          title: '日本三大岩場のひとつとされる',
          body: '谷川岳にある一ノ倉沢。みなかみ町観光協会は、日本三大岩場の一つと紹介している。',
          source: 'https://www.enjoy-minakami.jp/spot/8010/',
          mapQuery: '一ノ倉沢',
        },
        {
          id: 'ichinokurasawa-snow',
          x: 0.5,
          y: 0.7,
          title: '真夏に残る雪',
          body: '真夏に見られる一ノ倉沢の残雪は圧巻で、アルピニストを惹きつけてやまない絶景とされる。',
          source: 'https://www.enjoy-minakami.jp/spot/8010/',
          mapQuery: '谷川岳 一ノ倉沢',
        },
        {
          id: 'ichinokurasawa-access',
          x: 0.78,
          y: 0.55,
          title: '歩くか電気バスで',
          body: '一般車両の通行は年間を通して禁止されている。谷川岳山岳資料館を発着する電気バスが季節運行する。',
          source: 'https://www.enjoy-minakami.jp/spot/8010/',
          mapQuery: '谷川岳',
        },
      ],
    },
    {
      id: 'kakumanbuchi',
      name: '赤城山 覚満淵',
      area: '前橋市',
      image: '/assets/scenery/gunma/scenes/05_kakumanbuchi.webp',
      hotspots: [
        {
          id: 'kakumanbuchi-akagi',
          x: 0.28,
          y: 0.3,
          title: '標高1828メートルの山',
          body: '赤城山は標高1,828メートル。県立赤城公園の指定は昭和10年2月8日で、面積はおよそ1,290ヘクタール。',
          source: 'https://www.pref.gunma.jp/page/1222.html',
          mapQuery: '赤城山',
        },
        {
          id: 'kakumanbuchi-onuma',
          x: 0.55,
          y: 0.6,
          title: '大沼は火口原湖',
          body: '大沼は標高1,340メートルにあり、周囲4キロメートル、面積88万平方メートル、最大水深19メートルの火口原湖。小沼は爆裂火口の跡にできた火口湖。',
          source: 'https://www.pref.gunma.jp/page/1222.html',
          mapQuery: '赤城大沼',
        },
        {
          id: 'kakumanbuchi-kakuman',
          x: 0.8,
          y: 0.8,
          title: '古い大沼の名残',
          body: '覚満淵は大沼の南東側、標高1,360メートルに位置する沼と湿原で、かつての古大沼湖の一部が湿原化したもの。',
          source: 'https://www.pref.gunma.jp/page/1222.html',
          mapQuery: '覚満淵',
        },
      ],
    },
    {
      id: 'myogisan',
      name: '妙義山 石門',
      area: '下仁田町',
      image: '/assets/scenery/gunma/scenes/06_myogisan.webp',
      hotspots: [
        {
          id: 'myogisan-kisho',
          x: 0.28,
          y: 0.3,
          title: '日本三大奇勝のひとつとされる',
          body: '下仁田町は妙義山を日本三大奇勝の1つと紹介している。荒々しい岩肌がつくり出す自然景観の美しさが特徴。',
          source: 'https://www.town.shimonita.lg.jp/kanko/m03/m05/07.html',
          mapQuery: '妙義山',
        },
        {
          id: 'myogisan-joumou',
          x: 0.55,
          y: 0.6,
          title: '上毛三山のひとつ',
          body: '赤城山、榛名山とともに上毛三山に数えられ、群馬を代表する山のひとつ。',
          source: 'https://www.town.shimonita.lg.jp/kanko/m03/m05/07.html',
          mapQuery: '下仁田町 妙義山',
        },
        {
          id: 'myogisan-peaks',
          x: 0.8,
          y: 0.75,
          title: '表妙義と裏妙義',
          body: '表妙義は金洞山・白雲山・金鶏山、裏妙義は谷急山・丁須の頭・御岳からなる。最高峰は白雲山相馬岳の1,104メートル、金洞山東岳が1,094メートル。見どころは第一石門から第四石門までの石門群と「日暮しの景」。',
          source: 'https://www.town.shimonita.lg.jp/kanko/m03/m05/07.html',
          mapQuery: '妙義山 石門',
        },
      ],
    },
    {
      id: 'meganebashi',
      name: 'めがね橋',
      area: '安中市',
      image: '/assets/scenery/gunma/scenes/07_meganebashi.webp',
      hotspots: [
        {
          id: 'meganebashi-built',
          x: 0.28,
          y: 0.35,
          title: '明治25年に完成',
          body: '明治25年4月に建設が始まり、12月に完成した。',
          source: 'https://www.city.annaka.lg.jp/page/2018.html',
          mapQuery: 'めがね橋 碓氷第三橋梁',
        },
        {
          id: 'meganebashi-height',
          x: 0.55,
          y: 0.65,
          title: '川底からの高さ31メートル',
          body: '川底からの高さが31メートルの、4連の雄大なアーチ橋である。',
          source: 'https://www.city.annaka.lg.jp/page/2018.html',
          mapQuery: '碓氷第三橋梁',
        },
        {
          id: 'meganebashi-brick',
          x: 0.8,
          y: 0.4,
          title: 'すべて煉瓦造',
          body: 'すべてが煉瓦造りで、国の重要文化財に指定されている。我が国最大級のレンガアーチ橋。碓氷峠には第2橋梁から第7橋梁、および第13橋梁までの7基が残っている。',
          source: 'https://www.city.annaka.lg.jp/page/2018.html',
          mapQuery: '安中市 アプトの道',
        },
      ],
    },
    {
      id: 'kusatsu-yubatake',
      name: '草津温泉 湯畑',
      area: '草津町',
      image: '/assets/scenery/gunma/scenes/08_kusatsu.webp',
      hotspots: [
        {
          id: 'kusatsu-yubatake-volume',
          x: 0.3,
          y: 0.4,
          title: '毎分4000リットル',
          body: '毎分4,000リットルもの温泉湧出量を誇る、草津温泉のシンボル。',
          source: 'https://www.town.kusatsu.gunma.jp/www/contents/1485303527539/index.html',
          mapQuery: '草津温泉 湯畑',
        },
        {
          id: 'kusatsu-yubatake-kaori',
          x: 0.58,
          y: 0.65,
          title: 'かおり風景100選',
          body: '平成14年に「かおり風景100選」に選定された。',
          source: 'https://www.town.kusatsu.gunma.jp/www/contents/1485303527539/index.html',
          mapQuery: '草津町 湯畑',
        },
        {
          id: 'kusatsu-yubatake-meisho',
          x: 0.8,
          y: 0.35,
          title: '温泉街の中心が国の名勝',
          body: '平成29年に国の文化財「名勝」に指定された。夜は光と湯気が重なって幻想的になる。',
          source: 'https://www.town.kusatsu.gunma.jp/www/contents/1485303527539/index.html',
          mapQuery: '草津温泉',
        },
      ],
    },
    {
      id: 'ikaho-ishidan',
      name: '伊香保温泉 石段街',
      area: '渋川市',
      image: '/assets/scenery/gunma/scenes/09_ikaho.webp',
      hotspots: [
        {
          id: 'ikaho-ishidan-history',
          x: 0.28,
          y: 0.35,
          title: '天正4年に形づくられた',
          body: 'この石段街は約400年の歴史をもち、長篠の戦いの翌年、天正4年（1576年）に形成されたという。',
          source:
            'https://www.city.shibukawa.lg.jp/kankou/kankou/ikahoonsen/streetsofikaho/p000206.html',
          mapQuery: '伊香保温泉 石段街',
        },
        {
          id: 'ikaho-ishidan-yutoi',
          x: 0.55,
          y: 0.65,
          title: '石段の下に湯樋が通る',
          body: '新たに湯元から温泉を引き、石段を造って中央に湯樋を伏せ、左右に整然と建ち並んだ各々の屋敷に引湯して温泉宿として経営した。',
          source:
            'https://www.city.shibukawa.lg.jp/kankou/kankou/ikahoonsen/streetsofikaho/p000206.html',
          mapQuery: '渋川市 伊香保',
        },
        {
          id: 'ikaho-ishidan-365',
          x: 0.78,
          y: 0.28,
          title: '365段になったわけ',
          body: '平成22年には、1年365日賑わうようにとの願いを込めて、北側に石段を追加して365段とした。',
          source:
            'https://www.city.shibukawa.lg.jp/kankou/kankou/ikahoonsen/streetsofikaho/p000206.html',
          mapQuery: '伊香保 石段',
        },
      ],
    },
    {
      id: 'rakusanen',
      name: '楽山園',
      area: '甘楽町',
      image: '/assets/scenery/gunma/scenes/10_rakusanen.webp',
      hotspots: [
        {
          id: 'rakusanen-meisho',
          x: 0.28,
          y: 0.35,
          title: '平成12年に国指定名勝',
          body: '平成12年（2000年）3月30日に国指定名勝となった。面積は23,437.33平方メートル。',
          source: 'https://www.town.kanra.lg.jp/kyouiku/bunkazai/map/20120330191558.html',
          mapQuery: '楽山園',
        },
        {
          id: 'rakusanen-oda',
          x: 0.55,
          y: 0.62,
          title: '織田信雄が造営',
          body: '織田信雄が元和元年（1615年）に小幡の地を拝領して造営した庭園である。',
          source: 'https://www.town.kanra.lg.jp/kyouiku/bunkazai/map/20120330191558.html',
          mapQuery: '甘楽町 楽山園',
        },
        {
          id: 'rakusanen-shakkei',
          x: 0.8,
          y: 0.78,
          title: '山を借りる庭',
          body: '池泉回遊式の借景庭園で、雄川をはさんだ紅葉山、南方の連石山、熊倉山を景色に取り込んでいる。全国的にも珍しい五角形の腰掛茶屋がある。',
          source: 'https://www.town.kanra.lg.jp/kyouiku/bunkazai/map/20120330191558.html',
          mapQuery: '楽山園 腰掛茶屋',
        },
      ],
    },
  ],
};
